"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowLeft, ArrowRight } from "@untitledui/icons";
import { ButtonGroup, ButtonGroupItem } from "@/components/base/button-group/button-group";
import { Button } from "@/components/base/buttons/button";
import { useBreakpoint } from "@/hooks/use-breakpoint";
import { cx } from "@/utils/cx";
import type { PaginationRootProps } from "./pagination-base";
import { Pagination } from "./pagination-base";

/** Secondary pagination arrows — enabled state must read clearly vs disabled gray. */
const paginationNavButtonClassName = "enabled:text-fg-primary enabled:*:data-icon:text-fg-primary hover:enabled:*:data-icon:text-fg-primary hover:enabled:bg-primary_hover";
interface PaginationProps extends Partial<Omit<PaginationRootProps, "children">> {
  /** Whether the pagination buttons are rounded. */
  rounded?: boolean;
}
const PaginationItem = t0 => {
  const $ = _c(6);
  const {
    value,
    rounded,
    isCurrent
  } = t0;
  let t1;
  if ($[0] !== rounded) {
    t1 = t2 => {
      const {
        isSelected
      } = t2;
      return cx("flex size-10 cursor-pointer items-center justify-center p-3 text-sm font-medium text-quaternary outline-focus-ring transition duration-100 ease-linear hover:bg-primary_hover hover:text-secondary focus-visible:z-10 focus-visible:bg-primary_hover focus-visible:outline-2 focus-visible:outline-offset-2", rounded ? "rounded-full" : "rounded-lg", isSelected && "bg-primary_hover text-secondary");
    };
    $[0] = rounded;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isCurrent || $[3] !== t1 || $[4] !== value) {
    t2 = <Pagination.Item value={value} isCurrent={isCurrent} className={t1}>{value}</Pagination.Item>;
    $[2] = isCurrent;
    $[3] = t1;
    $[4] = value;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
interface MobilePaginationProps {
  /** The current page. */
  page?: number;
  /** The total number of pages. */
  total?: number;
  /** The class name of the pagination component. */
  className?: string;
  /** The function to call when the page changes. */
  onPageChange?: (page: number) => void;
}
const MobilePagination = t0 => {
  const $ = _c(21);
  const {
    page: t1,
    total: t2,
    className,
    onPageChange
  } = t0;
  const page = t1 === undefined ? 1 : t1;
  const total = t2 === undefined ? 10 : t2;
  let t3;
  if ($[0] !== className) {
    t3 = cx("flex items-center justify-between md:hidden", className);
    $[0] = className;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== onPageChange || $[3] !== page) {
    t4 = <Button aria-label="Go to previous page" iconLeading={ArrowLeft} color="secondary" size="sm" className={paginationNavButtonClassName} onClick={() => onPageChange?.(Math.max(0, page - 1))} />;
    $[2] = onPageChange;
    $[3] = page;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== page) {
    t5 = <span className="font-medium">{page}</span>;
    $[5] = page;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== total) {
    t6 = <span className="font-medium">{total}</span>;
    $[7] = total;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <span className="text-sm text-fg-secondary">Page {t5} of{" "}{t6}</span>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== onPageChange || $[13] !== page || $[14] !== total) {
    t8 = <Button aria-label="Go to next page" iconTrailing={ArrowRight} color="secondary" size="sm" className={paginationNavButtonClassName} onClick={() => onPageChange?.(Math.min(total, page + 1))} />;
    $[12] = onPageChange;
    $[13] = page;
    $[14] = total;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t3 || $[17] !== t4 || $[18] !== t7 || $[19] !== t8) {
    t9 = <nav aria-label="Pagination" className={t3}>{t4}{t7}{t8}</nav>;
    $[16] = t3;
    $[17] = t4;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
};
export const PaginationPageDefault = t0 => {
  const $ = _c(28);
  let className;
  let props;
  let rounded;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      rounded,
      page: t1,
      total: t2,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = rounded;
    $[4] = t1;
    $[5] = t2;
  } else {
    className = $[1];
    props = $[2];
    rounded = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const page = t1 === undefined ? 1 : t1;
  const total = t2 === undefined ? 10 : t2;
  const isDesktop = useBreakpoint("md");
  let t3;
  if ($[6] !== className) {
    t3 = cx("flex w-full items-center justify-between gap-3 border-t border-secondary pt-4 md:pt-5", className);
    $[6] = className;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = isDesktop ? "Previous" : undefined;
  let t5;
  if ($[8] !== t4) {
    t5 = <div className="hidden flex-1 justify-start md:flex"><Pagination.PrevTrigger asChild={true}><Button aria-label="Go to previous page" iconLeading={ArrowLeft} color="link-gray" size="sm">{t4}{" "}</Button></Pagination.PrevTrigger></div>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = isDesktop ? "Previous" : undefined;
  let t7;
  if ($[10] !== t6) {
    t7 = <Pagination.PrevTrigger asChild={true} className="md:hidden"><Button aria-label="Go to previous page" iconLeading={ArrowLeft} color="secondary" size="sm" className={paginationNavButtonClassName}>{t6}</Button></Pagination.PrevTrigger>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== rounded) {
    t8 = <Pagination.Context>{t9 => {
        const {
          pages,
          currentPage,
          total: total_0
        } = t9;
        return <><div className="hidden justify-center gap-0.5 md:flex">{pages.map((page_0, index) => page_0.type === "page" ? <PaginationItem key={index} rounded={rounded} {...page_0} /> : <Pagination.Ellipsis key={index} className="flex size-10 shrink-0 items-center justify-center text-tertiary">…</Pagination.Ellipsis>)}</div><div className="flex justify-center text-sm whitespace-pre text-fg-secondary md:hidden">Page <span className="font-medium">{currentPage}</span> of{" "}<span className="font-medium">{total_0}</span></div></>;
      }}</Pagination.Context>;
    $[12] = rounded;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = isDesktop ? "Next" : undefined;
  let t10;
  if ($[14] !== t9) {
    t10 = <div className="hidden flex-1 justify-end md:flex"><Pagination.NextTrigger asChild={true}><Button aria-label="Go to next page" iconTrailing={ArrowRight} color="link-gray" size="sm">{t9}</Button></Pagination.NextTrigger></div>;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const t11 = isDesktop ? "Next" : undefined;
  let t12;
  if ($[16] !== t11) {
    t12 = <Pagination.NextTrigger asChild={true} className="md:hidden"><Button aria-label="Go to next page" iconTrailing={ArrowRight} color="secondary" size="sm" className={paginationNavButtonClassName}>{t11}</Button></Pagination.NextTrigger>;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== page || $[19] !== props || $[20] !== t10 || $[21] !== t12 || $[22] !== t3 || $[23] !== t5 || $[24] !== t7 || $[25] !== t8 || $[26] !== total) {
    t13 = <Pagination.Root {...props} page={page} total={total} className={t3}>{t5}{t7}{t8}{t10}{t12}</Pagination.Root>;
    $[18] = page;
    $[19] = props;
    $[20] = t10;
    $[21] = t12;
    $[22] = t3;
    $[23] = t5;
    $[24] = t7;
    $[25] = t8;
    $[26] = total;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  return t13;
};
export const PaginationPageMinimalCenter = t0 => {
  const $ = _c(22);
  let className;
  let props;
  let rounded;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      rounded,
      page: t1,
      total: t2,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = rounded;
    $[4] = t1;
    $[5] = t2;
  } else {
    className = $[1];
    props = $[2];
    rounded = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const page = t1 === undefined ? 1 : t1;
  const total = t2 === undefined ? 10 : t2;
  const isDesktop = useBreakpoint("md");
  let t3;
  if ($[6] !== className) {
    t3 = cx("flex w-full items-center justify-between gap-3 border-t border-secondary pt-4 md:pt-5", className);
    $[6] = className;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = isDesktop ? "Previous" : undefined;
  let t5;
  if ($[8] !== t4) {
    t5 = <div className="flex flex-1 justify-start"><Pagination.PrevTrigger asChild={true}><Button aria-label="Go to previous page" iconLeading={ArrowLeft} color="secondary" size="sm" className={paginationNavButtonClassName}>{t4}</Button></Pagination.PrevTrigger></div>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== rounded) {
    t6 = <Pagination.Context>{t7 => {
        const {
          pages,
          currentPage,
          total: total_0
        } = t7;
        return <><div className="hidden justify-center gap-0.5 md:flex">{pages.map((page_0, index) => page_0.type === "page" ? <PaginationItem key={index} rounded={rounded} {...page_0} /> : <Pagination.Ellipsis key={index} className="flex size-10 shrink-0 items-center justify-center text-tertiary">…</Pagination.Ellipsis>)}</div><div className="flex justify-center text-sm whitespace-pre text-fg-secondary md:hidden">Page <span className="font-medium">{currentPage}</span> of{" "}<span className="font-medium">{total_0}</span></div></>;
      }}</Pagination.Context>;
    $[10] = rounded;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = isDesktop ? "Next" : undefined;
  let t8;
  if ($[12] !== t7) {
    t8 = <div className="flex flex-1 justify-end"><Pagination.NextTrigger asChild={true}><Button aria-label="Go to next page" iconTrailing={ArrowRight} color="secondary" size="sm" className={paginationNavButtonClassName}>{t7}</Button></Pagination.NextTrigger></div>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== page || $[15] !== props || $[16] !== t3 || $[17] !== t5 || $[18] !== t6 || $[19] !== t8 || $[20] !== total) {
    t9 = <Pagination.Root {...props} page={page} total={total} className={t3}>{t5}{t6}{t8}</Pagination.Root>;
    $[14] = page;
    $[15] = props;
    $[16] = t3;
    $[17] = t5;
    $[18] = t6;
    $[19] = t8;
    $[20] = total;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
};
export const PaginationCardDefault = t0 => {
  const $ = _c(22);
  let className;
  let props;
  let rounded;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      rounded,
      page: t1,
      total: t2,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = rounded;
    $[4] = t1;
    $[5] = t2;
  } else {
    className = $[1];
    props = $[2];
    rounded = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const page = t1 === undefined ? 1 : t1;
  const total = t2 === undefined ? 10 : t2;
  const isDesktop = useBreakpoint("md");
  let t3;
  if ($[6] !== className) {
    t3 = cx("flex w-full items-center justify-between gap-3 border-t border-secondary px-4 py-3 md:px-6 md:pt-3 md:pb-4", className);
    $[6] = className;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = isDesktop ? "Previous" : undefined;
  let t5;
  if ($[8] !== t4) {
    t5 = <div className="flex flex-1 justify-start"><Pagination.PrevTrigger asChild={true}><Button aria-label="Go to previous page" iconLeading={ArrowLeft} color="secondary" size="sm" className={paginationNavButtonClassName}>{t4}</Button></Pagination.PrevTrigger></div>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== rounded) {
    t6 = <Pagination.Context>{t7 => {
        const {
          pages,
          currentPage,
          total: total_0
        } = t7;
        return <><div className="hidden justify-center gap-0.5 md:flex">{pages.map((page_0, index) => page_0.type === "page" ? <PaginationItem key={index} rounded={rounded} {...page_0} /> : <Pagination.Ellipsis key={index} className="flex size-10 shrink-0 items-center justify-center text-tertiary">…</Pagination.Ellipsis>)}</div><div className="flex justify-center text-sm whitespace-pre text-fg-secondary md:hidden">Page <span className="font-medium">{currentPage}</span> of{" "}<span className="font-medium">{total_0}</span></div></>;
      }}</Pagination.Context>;
    $[10] = rounded;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = isDesktop ? "Next" : undefined;
  let t8;
  if ($[12] !== t7) {
    t8 = <div className="flex flex-1 justify-end"><Pagination.NextTrigger asChild={true}><Button aria-label="Go to next page" iconTrailing={ArrowRight} color="secondary" size="sm" className={paginationNavButtonClassName}>{t7}</Button></Pagination.NextTrigger></div>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== page || $[15] !== props || $[16] !== t3 || $[17] !== t5 || $[18] !== t6 || $[19] !== t8 || $[20] !== total) {
    t9 = <Pagination.Root {...props} page={page} total={total} className={t3}>{t5}{t6}{t8}</Pagination.Root>;
    $[14] = page;
    $[15] = props;
    $[16] = t3;
    $[17] = t5;
    $[18] = t6;
    $[19] = t8;
    $[20] = total;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
};
interface PaginationCardMinimalProps {
  /** The current page. */
  page?: number;
  /** The total number of pages. */
  total?: number;
  /** The alignment of the pagination. */
  align?: "left" | "center" | "right";
  /** The class name of the pagination component. */
  className?: string;
  /** The function to call when the page changes. */
  onPageChange?: (page: number) => void;
}
export const PaginationCardMinimal = t0 => {
  const $ = _c(47);
  const {
    page: t1,
    total: t2,
    align: t3,
    onPageChange,
    className
  } = t0;
  const page = t1 === undefined ? 1 : t1;
  const total = t2 === undefined ? 10 : t2;
  const align = t3 === undefined ? "left" : t3;
  let t4;
  if ($[0] !== className) {
    t4 = cx("border-t border-secondary px-4 py-3 md:px-6 md:pt-3 md:pb-4", className);
    $[0] = className;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== onPageChange || $[3] !== page || $[4] !== total) {
    t5 = <MobilePagination page={page} total={total} onPageChange={onPageChange} />;
    $[2] = onPageChange;
    $[3] = page;
    $[4] = total;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = align === "center" && "justify-between";
  let t7;
  if ($[6] !== t6) {
    t7 = cx("hidden items-center gap-3 md:flex", t6);
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = align === "center" && "flex flex-1 justify-start";
  let t9;
  if ($[8] !== t8) {
    t9 = cx(t8);
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const t10 = page === 1;
  let t11;
  if ($[10] !== onPageChange || $[11] !== page) {
    t11 = () => onPageChange?.(Math.max(0, page - 1));
    $[10] = onPageChange;
    $[11] = page;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== t10 || $[14] !== t11) {
    t12 = <Button isDisabled={t10} color="secondary" size="sm" className={paginationNavButtonClassName} onClick={t11}>Previous</Button>;
    $[13] = t10;
    $[14] = t11;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t12 || $[17] !== t9) {
    t13 = <div className={t9}>{t12}</div>;
    $[16] = t12;
    $[17] = t9;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const t14 = align === "right" && "order-first mr-auto";
  const t15 = align === "left" && "order-last ml-auto";
  let t16;
  if ($[19] !== t14 || $[20] !== t15) {
    t16 = cx("text-sm font-medium text-fg-secondary", t14, t15);
    $[19] = t14;
    $[20] = t15;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== page || $[23] !== t16 || $[24] !== total) {
    t17 = <span className={t16}>Page {page} of {total}</span>;
    $[22] = page;
    $[23] = t16;
    $[24] = total;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  const t18 = align === "center" && "flex flex-1 justify-end";
  let t19;
  if ($[26] !== t18) {
    t19 = cx(t18);
    $[26] = t18;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  const t20 = page === total;
  let t21;
  if ($[28] !== onPageChange || $[29] !== page || $[30] !== total) {
    t21 = () => onPageChange?.(Math.min(total, page + 1));
    $[28] = onPageChange;
    $[29] = page;
    $[30] = total;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  let t22;
  if ($[32] !== t20 || $[33] !== t21) {
    t22 = <Button isDisabled={t20} color="secondary" size="sm" className={paginationNavButtonClassName} onClick={t21}>Next</Button>;
    $[32] = t20;
    $[33] = t21;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  let t23;
  if ($[35] !== t19 || $[36] !== t22) {
    t23 = <div className={t19}>{t22}</div>;
    $[35] = t19;
    $[36] = t22;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] !== t13 || $[39] !== t17 || $[40] !== t23 || $[41] !== t7) {
    t24 = <nav aria-label="Pagination" className={t7}>{t13}{t17}{t23}</nav>;
    $[38] = t13;
    $[39] = t17;
    $[40] = t23;
    $[41] = t7;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== t24 || $[44] !== t4 || $[45] !== t5) {
    t25 = <div className={t4}>{t5}{t24}</div>;
    $[43] = t24;
    $[44] = t4;
    $[45] = t5;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  return t25;
};
interface PaginationButtonGroupProps extends Partial<Omit<PaginationRootProps, "children">> {
  /** The alignment of the pagination. */
  align?: "left" | "center" | "right";
}
export const PaginationButtonGroup = t0 => {
  const $ = _c(19);
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      align: t1,
      page: t2,
      total: t3,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    props = $[1];
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  const align = t1 === undefined ? "left" : t1;
  const page = t2 === undefined ? 1 : t2;
  const total = t3 === undefined ? 10 : t3;
  const isDesktop = useBreakpoint("md");
  const t4 = align === "left" && "justify-start";
  const t5 = align === "center" && "justify-center";
  const t6 = align === "right" && "justify-end";
  let t7;
  if ($[5] !== t4 || $[6] !== t5 || $[7] !== t6) {
    t7 = cx("flex border-t border-secondary px-4 py-3 md:px-6 md:pt-3 md:pb-4", t4, t5, t6);
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== isDesktop) {
    t8 = <Pagination.Context>{t9 => {
        const {
          pages
        } = t9;
        return <ButtonGroup size="md"><Pagination.PrevTrigger asChild={true}><ButtonGroupItem aria-label="Go to previous page" iconLeading={ArrowLeft}>{isDesktop ? "Previous" : undefined}</ButtonGroupItem></Pagination.PrevTrigger>{pages.map(_temp)}<Pagination.NextTrigger asChild={true}><ButtonGroupItem aria-label="Go to next page" iconTrailing={ArrowRight}>{isDesktop ? "Next" : undefined}</ButtonGroupItem></Pagination.NextTrigger></ButtonGroup>;
      }}</Pagination.Context>;
    $[9] = isDesktop;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== page || $[12] !== props || $[13] !== t8 || $[14] !== total) {
    t9 = <Pagination.Root {...props} page={page} total={total}>{t8}</Pagination.Root>;
    $[11] = page;
    $[12] = props;
    $[13] = t8;
    $[14] = total;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t7 || $[17] !== t9) {
    t10 = <div className={t7}>{t9}</div>;
    $[16] = t7;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
};
function _temp(page_0, index) {
  return page_0.type === "page" ? <Pagination.Item key={index} {...page_0} asChild={true}><ButtonGroupItem isSelected={page_0.isCurrent} className="size-10 items-center justify-center">{page_0.value}</ButtonGroupItem></Pagination.Item> : <Pagination.Ellipsis key={index}><ButtonGroupItem className="pointer-events-none size-10 items-center justify-center rounded-none!">…</ButtonGroupItem></Pagination.Ellipsis>;
}